import React from 'react';
import {
  Grid,
  makeStyles,
  TextField,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import HelpOutlinedIcon from '@material-ui/icons/HelpOutline';
import { Controller, useForm } from 'react-hook-form';
import useUserActionLog, {
  Enum_Action_Button_Enum,
  CapaActivityType
} from '@hooks/useUserActionLog';
import { STORAGE_ACTION, getStorageItem } from '@utils/storage';
import {
  CapaQuotationUserType,
  useSuggestServiceMutation
} from '@generated/graphql';
import {
  CustomizedDialog,
  CustomizedTypography,
  CustomizedButton
} from '@components/Customized';
import { emailRegex } from '@utils/regex';
const useStyles = makeStyles((theme) => {
  return {
    DialogTitle: {
      ...theme.typography.body2,
      color: theme.palette.text.secondary,
      wordBreak: 'break-all',
      marginBottom: 32
    },
    DialogSubtitle: {
      ...theme.typography.body2,
      color: theme.palette.text.secondary,
      wordBreak: 'break-all',
      marginBottom: 12
    },
    textfieldRoot: {
      marginBottom: 32
    },
    textfieldLabel: {
      ...theme.typography.body1,
      marginBottom: 12,
      fontWeight: 700
    },
    textfield: {
      '& .input': {
        '&::placeholder': {
          ...theme.typography.body2,
          color: theme.palette.text.secondary
        }
      }
    },
    checkbox: {
      marginLeft: 9
    },
    checkboxLabel: {
      ...theme.typography.body2,
      color: theme.palette.text.primary,
      fontWeight: 400
    },
    submitButton: {
      marginTop: 32,
      height: 48
    }
  };
});

interface UseFormProps {
  service: string;
  email: string;
  agree: boolean;
}

interface ServiceSurveyDialogProps {
  open: boolean;
  onCompleteOpen: () => void;
  onClose: () => void;
}
const ServiceSurveyDialog = (props: ServiceSurveyDialogProps) => {
  const matches = useMediaQuery(useTheme().breakpoints.down('xs'));
  const isGhost = getStorageItem(STORAGE_ACTION.IS_GHOST_CLIENT);
  const { onInsertUserActionLogOne } = useUserActionLog();
  const classes = useStyles();
  const { control, handleSubmit, errors, watch } = useForm<UseFormProps>({
    defaultValues: { service: '', email: '', agree: false }
  });
  const [suggestServiceMutation] = useSuggestServiceMutation();
  const serviceFieldError = errors && errors['service'] ? true : false;
  const emailFieldError = errors && errors['email'] ? true : false;
  const agreeValue = watch('agree');
  const onSubmit = (params: UseFormProps) => {
    if (isGhost && isGhost === 'true') return;
    if (!params.agree) return;

    onInsertUserActionLogOne(
      Enum_Action_Button_Enum.Button_RFQ_Survey_Submit,
      CapaActivityType.ButtonClick
    );

    try {
      suggestServiceMutation({
        variables: {
          input: {
            email: params.email.length < 1 ? null : params.email,
            suggestion: params.service,
            userType: CapaQuotationUserType.Client
          }
        }
      }).then(() => {
        props.onCompleteOpen();
      });
    } catch (e) {
      props.onClose();
    }
  };

  return (
    <CustomizedDialog
      open={props.open}
      maxWidth={matches ? 343 : 476}
      onClose={props.onClose}
      color="primary"
      title="서비스 개설 제안"
      closeButton
      icon={<HelpOutlinedIcon />}
    >
      <Grid container>
        <CustomizedTypography className={classes.DialogTitle}>
          캐파에서 제공받고 싶은 서비스가 있지만 없으셨나요? 개설되길 원하는
          서비스를 작성하여 알려주세요. 보내주신 의견은 조사 및 검토 후
          반영하겠습니다.
        </CustomizedTypography>
      </Grid>

      <Grid container>
        <Controller
          name="service"
          control={control}
          defaultValue=""
          rules={{
            required: true
          }}
          render={(renderProps) => {
            return (
              <Grid item xs={12} className={classes.textfieldRoot}>
                <CustomizedTypography className={classes.textfieldLabel}>
                  어떤 서비스를 찾으시나요? *
                </CustomizedTypography>
                <TextField
                  className={classes.textfield}
                  {...renderProps}
                  error={serviceFieldError}
                  variant="outlined"
                  fullWidth
                  size="small"
                  placeholder="희망하시는 제조 서비스를 적어주세요"
                  inputProps={{
                    className: 'input'
                  }}
                />
              </Grid>
            );
          }}
        />

        <Controller
          name="email"
          control={control}
          defaultValue=""
          rules={{
            pattern: {
              value: emailRegex,
              message: '올바른 이메일 형식으로 입력해주세요.'
            }
          }}
          render={(renderProps) => {
            return (
              <Grid item xs={12} className={classes.textfieldRoot}>
                <CustomizedTypography className={classes.textfieldLabel}>
                  서비스 개설 안내를 원하신다면 이메일 주소를 남겨주세요.
                </CustomizedTypography>
                <TextField
                  error={emailFieldError}
                  className={classes.textfield}
                  {...renderProps}
                  variant="outlined"
                  fullWidth
                  size="small"
                  placeholder="알림을 받으실 이메일 주소를 적어주세요 (선택)"
                  inputProps={{
                    className: 'input',
                    type: 'email'
                  }}
                />
              </Grid>
            );
          }}
        />
      </Grid>

      <Grid container>
        <CustomizedTypography className={classes.DialogSubtitle}>
          수집된 의견과 개인정보는 제출하신 날로부터 1년 뒤 파기되며, 해당
          내용을 충분히 숙지하셨다면 아래 체크박스를 클릭해 주세요. 서비스 제안
          외 즉시 문제 해결이 필요한 경우에는 채널톡으로 문의주세요.
        </CustomizedTypography>
      </Grid>

      <Grid container>
        <FormControlLabel
          classes={{ label: classes.checkboxLabel }}
          labelPlacement="end"
          control={
            <Controller
              control={control}
              name="agree"
              defaultValue={false}
              render={(renderProps) => {
                return (
                  <Checkbox
                    className={classes.checkbox}
                    {...renderProps}
                    onChange={(e) => renderProps.onChange(e.target.checked)}
                    checked={renderProps.value}
                    color="primary"
                    name="agree"
                  />
                );
              }}
            />
          }
          label="네, 확인하였습니다."
        />
      </Grid>

      <Grid container>
        <CustomizedButton
          className={classes.submitButton}
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          disabled={!agreeValue}
          onClick={handleSubmit(onSubmit)}
        >
          제출하기
        </CustomizedButton>
      </Grid>
    </CustomizedDialog>
  );
};

export default ServiceSurveyDialog;
